import type { PageResult, Post } from "../types";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";
const LIMIT = 15;

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * ✅ REAL BACKEND FETCH — MATCHES YOUR EXPRESS API:
 * GET /api/posts?page=1&limit=20
 */
export async function fetchPostsPage(
  cursor?: string | null,
  options: {
    spaceId?: string;
    limit?: number;
    filter?: string;
    q?: string;
    sort?: string;
    author?: string;
    signal?: AbortSignal;
  } = {}
): Promise<PageResult<Post>> {
  const page = cursor ? Number(cursor) : 1;

  const url = new URL(`${API_BASE}/api/posts`);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(options.limit || LIMIT));
  if (options.spaceId) url.searchParams.set("spaceId", options.spaceId);
  if (options.filter && options.filter !== "all") url.searchParams.set("filter", options.filter);
  if (options.q) url.searchParams.set("q", options.q);
  if (options.sort) url.searchParams.set("sort", options.sort);
  if (options.author) url.searchParams.set("author", options.author);

  const res = await fetch(url.toString(), {
    headers: authHeaders(),
    signal: options.signal,
  });

  if (!res.ok) {
    throw new Error(`Posts error: ${res.status}`);
  }

  const data = await res.json();

  return {
    items: data.posts as Post[],
    nextCursor: data.hasMore ? String(data.page + 1) : null,
    total: data.total,
    page: data.page,
    limit: data.limit,
  };
}

export async function fetchPost(postId: string): Promise<Post> {
  const res = await fetch(`${API_BASE}/api/posts/${encodeURIComponent(postId)}`, {
    headers: authHeaders(),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Post error: ${res.status}`);
  }

  return data.post as Post;
}

export type PostActivityKind = "comments" | "emotions";
export type PostActivity = {
  id: string;
  kind: "comment" | "emotion";
  createdAt: string;
  content: string;
  commentId: string | null;
  emotion: "like" | "love" | "dislike" | null;
  post: Post;
};

export async function fetchOwnPostActivity(
  kind: PostActivityKind,
  cursor?: string | null,
  signal?: AbortSignal
): Promise<{ items: PostActivity[]; nextCursor: string | null; username: string }> {
  const url = new URL(API_BASE + "/api/posts/me/activity");
  url.searchParams.set("kind", kind);
  url.searchParams.set("limit", "12");
  if (cursor) url.searchParams.set("cursor", cursor);
  const response = await fetch(url.toString(), { headers: authHeaders(), signal, cache: "no-store" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || "Could not load your activity");
  return {
    items: Array.isArray(data.items) ? data.items : [],
    nextCursor: data.nextCursor || null,
    username: String(data.username || ""),
  };
}

export async function createPost(input: {
  content: string;
  spaceId: string;
  spaceName: string;
  allowedActions?: string[];
  visibility?: string;
  schedule?: string;
  prompt?: string;
  isExclusive?: boolean;
}): Promise<Post> {
  const res = await fetch(`${API_BASE}/api/posts`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Create post failed: ${res.status}`);
  }

  return data.post as Post;
}

/**
 * Toggle reaction for a post (requires auth)
 * POST /api/posts/:postId/reactions
 * returns { success: true, reactions: {...}, userReaction: 'like' | 'love' | 'dislike' | null }
 */
export async function toggleReaction(postId: string, type: string) {
  const API = `${API_BASE}/api/posts/${postId}/reactions`;

  const res = await fetch(API, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ type }),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Toggle reaction failed: ${res.status} ${txt}`);
  }

  return res.json();
}

export async function removePostFromSpace(postId: string, reason = "") {
  const res = await fetch(`${API_BASE}/api/posts/${encodeURIComponent(postId)}/moderation/remove`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ reason }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Remove post failed: ${res.status}`);
  }
  return data;
}

export async function deletePost(postId: string, reason = "") {
  const res = await fetch(`${API_BASE}/api/posts/${encodeURIComponent(postId)}`, {
    method: "DELETE",
    headers: authHeaders(),
    body: JSON.stringify({ reason }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Delete post failed: ${res.status}`);
  }
  return data;
}

export async function updatePost(postId: string, content: string): Promise<Post> {
  const res = await fetch(`${API_BASE}/api/posts/${encodeURIComponent(postId)}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ content }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Edit post failed: ${res.status}`);
  }
  return data.post as Post;
}

export type HomeActivity = {
  id: string;
  kind: string;
  label: string;
  at: string;
};

export type TrendingTag = {
  tag: string;
  count: number;
};

export type TrendingSpace = {
  id: string;
  name: string;
  count: number;
};

export type DailyPrompt = {
  id: string;
  text: string;
  tag: string;
};

export type HomeSidebarData = {
  recentActivity: HomeActivity[];
  trendingTags: TrendingTag[];
  trendingSpaces: TrendingSpace[];
  dailyPrompt: DailyPrompt;
};

export async function fetchHomeSidebar(): Promise<HomeSidebarData> {
  const res = await fetch(`${API_BASE}/api/posts/sidebar`, {
    headers: authHeaders(),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Sidebar error: ${res.status}`);
  }

  return {
    recentActivity: data.recentActivity || [],
    trendingTags: data.trendingTags || [],
    trendingSpaces: data.trendingSpaces || [],
    dailyPrompt: data.dailyPrompt || {
      id: new Date().toISOString().slice(0, 10),
      text: "What's one tiny tool that 10x'd your workflow this week?",
      tag: "#daily-prompt",
    },
  };
}
